import React from 'react';
import { Box, Link, Typography } from '@mui/material';

const INLINE_MARKDOWN = /(\[[^\]]+\]\(https?:\/\/[^)\s]+\)|\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\*[^*]+\*|_[^_]+_)/g;

const renderInline = (text) => String(text || '').split(INLINE_MARKDOWN).filter(Boolean).map((part, index) => {
  const key = `${part}-${index}`;
  const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
  if (link) {
    return (
      <Link key={key} href={link[2]} target="_blank" rel="noopener noreferrer">
        {link[1]}
      </Link>
    );
  }
  if ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'))) {
    return <Box component="strong" key={key}>{part.slice(2, -2)}</Box>;
  }
  if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) {
    return <Box component="em" key={key}>{part.slice(1, -1)}</Box>;
  }
  if (part.startsWith('`') && part.endsWith('`')) {
    return (
      <Box
        component="code"
        key={key}
        sx={{ px: 0.5, py: 0.125, borderRadius: 0.5, bgcolor: 'action.hover', fontFamily: 'monospace' }}
      >
        {part.slice(1, -1)}
      </Box>
    );
  }
  return part;
});

const MarkdownContent = ({ children, variant = 'body2', color = 'text.primary', sx }) => {
  const lines = String(children || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let listItems = [];

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <Box component="ul" key={`list-${blocks.length}`} sx={{ my: 0.75, pl: 2.5 }}>
        {listItems.map((item, index) => (
          <Typography component="li" variant={variant} key={`${item}-${index}`} sx={{ mb: 0.25 }}>
            {renderInline(item)}
          </Typography>
        ))}
      </Box>
    );
    listItems = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const listMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (listMatch) {
      listItems.push(listMatch[1]);
      return;
    }
    flushList();
    if (!trimmed) return;

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      const headingVariant = headingMatch[1].length === 1 ? 'h6' : 'subtitle2';
      blocks.push(
        <Typography variant={headingVariant} component="h3" key={`line-${index}`} sx={{ mt: 1, mb: 0.5 }}>
          {renderInline(headingMatch[2])}
        </Typography>
      );
      return;
    }

    blocks.push(
      <Typography variant={variant} color={color} key={`line-${index}`} sx={{ mb: 0.75, whiteSpace: 'pre-wrap' }}>
        {renderInline(trimmed)}
      </Typography>
    );
  });
  flushList();

  return <Box sx={sx}>{blocks}</Box>;
};

export default MarkdownContent;
